import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClockIcon } from 'lucide-react';
const DAY_MS = 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'mrp_countdown_target';
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + DAY_MS;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed > Date.now()) return parsed;
    }
    const target = Date.now() + DAY_MS;
    window.localStorage.setItem(STORAGE_KEY, target.toString());
    return target;
  } catch {
    return Date.now() + DAY_MS;
  }
}
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(DAY_MS);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        const next = Date.now() + DAY_MS;
        try {
          window.localStorage.setItem(STORAGE_KEY, next.toString());
        } catch {
          /* storage unavailable — keep counting in memory */
        }
        setTarget(next);
        setRemaining(DAY_MS);
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = DAY_MS - remaining;
  const progress = Math.min(90, 65 + elapsed / DAY_MS * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.3
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="mt-10 max-w-3xl mx-auto bg-black border border-gold/40 rounded-2xl p-6 md:p-8 shadow-[0_0_30px_rgba(212,175,55,0.12)]" data-id="element-41" data-mp="2dpioy0q79f4i">
      <div className="text-center" data-id="element-42" data-mp="2ne66xm2oc11j">
        <p className="flex items-center justify-center gap-2 text-gold font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-43" data-mp="2jc1aygor6sbq">
          <AlarmClockIcon size={20} aria-hidden="true" data-id="element-44" data-mp="2ebwuurrrbzm1" />
          <span data-id="element-45" data-mp="29dvb04mibw9i">Il prezzo promozionale termina tra:</span>
        </p>

        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" role="timer" aria-live="off" aria-label={`Mancano ${hours} ore, ${minutes} minuti e ${seconds} secondi`} data-id="element-46" data-mp="29f6e0284ozzk">
          <TimeBox value={pad(hours)} label="ORE" data-id="element-47" data-mp="1vmguq5jzi965" />
          <span className="text-3xl md:text-5xl font-bold text-gold" aria-hidden="true" data-id="element-48" data-mp="1vo1mxoittg0u">
            :
          </span>
          <TimeBox value={pad(minutes)} label="MIN" data-id="element-49" data-mp="1qpgl5knxeoaa" />
          <span className="text-3xl md:text-5xl font-bold text-gold" aria-hidden="true" data-id="element-50" data-mp="1lo10htpnujsf">
            :
          </span>
          <TimeBox value={pad(seconds)} label="SEC" data-id="element-51" data-mp="15gq6a7dwgjg" />
        </div>

        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-gray-200 mb-8" data-id="element-52" data-mp="64tlljcbmvxn">
          Dopo, il prezzo potrà aumentare.
        </p>

        <div className="space-y-2" data-id="element-53" data-mp="b2bn4oo0e8vm">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-54" data-mp="f5rkxobtef9w">
            <span className="text-gold-light" data-id="element-55" data-mp="3eloeq73bh1yj">
              Accessi al prezzo promozionale
            </span>
            <span className="text-gold" data-id="element-56" data-mp="3jjg4ovkeie03">Disponibilità limitata</span>
          </div>
          <div className="w-full h-2.5 bg-white/10 rounded-full overflow-hidden border border-gold/30" data-id="element-57" data-mp="3nlb5lyyf6pdc">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.9,
            ease: [0.23, 1, 0.32, 1]
          }} className="h-full bg-gold rounded-full" data-id="element-58" data-mp="3sct8isk0700y" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-gold-light font-medium" data-id="element-59" data-mp="2gsotp7233o6v">
            {Math.round(progress)}% degli accessi disponibili già utilizzati
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-dark-card border-2 border-gold/40 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px]" data-id="element-60" data-mp="2lpoxfbbgoeut">
      <div className="flex items-baseline justify-center gap-1" data-id="element-61" data-mp="2zgabmmpz00md">
        <span className="text-4xl md:text-5xl font-bold text-white tabular-nums font-sans" data-id="element-62" data-mp="2ui0ei6koqpy4">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-gold-light" data-id="element-63" data-mp="39fjbtxbfibh6">
          {label}
        </span>
      </div>
    </div>;
}